import { Link } from "react-router-dom";
import { ShieldAlert, Users, Palette, Mic2, ArrowRight } from "lucide-react";
import { GoldBadge, Bullets, Reveal } from "@/components/shared";

const INITIATIVES = [
  {
    code: "BR-01",
    icon: ShieldAlert,
    name: "Predator Prevention Team (PPT)",
    tagline: "Eyes on the street. Boots on the asphalt.",
    image: "/assets/ppt-ribbon.jpg",
    description:
      "Direct street-level car and foot patrols running community safety initiatives to deter threats on the asphalt — before they reach a child's door.",
    bullets: [
      "Street-level car and foot patrols across high-risk community zones",
      "Visible deterrence of predatory behaviour in public spaces",
      "Documented observation and escalation protocols with authorities",
      "Community safety initiatives run in partnership with local residents",
    ],
    testId: "initiative-ppt-module",
  },
  {
    code: "BR-02",
    icon: Users,
    name: "Soldiers of P.A.I.N.",
    tagline: "A brotherhood and sisterhood forged in survival.",
    image: "/assets/soldiers-ribbon.jpg",
    description:
      "A dedicated social and community club focused intensely on child abuse prevention, mental health awareness, and suicide prevention.",
    bullets: [
      "Community club membership open to survivors, families, and allies",
      "Child abuse prevention campaigns and public awareness drives",
      "Mental health awareness programs led by lived-experience members",
      "Suicide prevention outreach and peer-support networks",
    ],
    testId: "initiative-soldiers-module",
  },
  {
    code: "BR-03",
    icon: Palette,
    name: "Broken Crayons",
    tagline: "Broken pieces can still draw beautiful futures.",
    appUrl: "https://safety-first-165.preview.emergentagent.com",
    image: "/assets/broken-crayons-logo.jpg",
    description:
      "A specialized initiative targeting early childhood and youth support — meeting young people where they are, before the damage sets in.",
    bullets: [
      "Early childhood and youth support programs for at-risk young people",
      "Trauma-informed mentoring and creative recovery activities",
      "Safe spaces where children are heard, believed, and protected",
      "Pathways from crisis into education, community, and stability",
    ],
    testId: "initiative-crayons-module",
  },
  {
    code: "BR-04",
    icon: Mic2,
    name: "Born From Broken",
    tagline: "From state ward to Royal Commission victory.",
    description:
      "The official autobiographical campaign, media portal, and anthem hub mapping Ronald Waters' 45-year journey from state ward to historical Royal Commission victory.",
    bullets: [
      "Official autobiography documenting 45 years of institutional truth",
      "Media portal with interviews, archives, and campaign updates",
      "Companion anthem tracks streaming through the campaign hub",
      "The public record of a survivor who refused to stay buried",
    ],
    testId: "initiative-born-module",
  },
];

export default function Initiatives() {
  return (
    <div data-testid="initiatives-page">
      <section className="border-b border-white/10 bg-[#111622] grain-overlay relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid gap-10 lg:grid-cols-[1fr_320px] lg:items-center">
          <Reveal>
            <GoldBadge testId="initiatives-badge">Operational Arms of P.A.I.N.</GoldBadge>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50">
              Core Sub-Branches <span className="gold-text-gradient">& Initiatives</span>
            </h1>
            <p className="mt-6 max-w-3xl text-base sm:text-lg leading-relaxed text-slate-300">
              Four dedicated entities, one mandate: protect the vulnerable, support the broken, and
              never let silence win again.
            </p>
            <p className="mt-4 font-display text-base sm:text-lg italic text-amber-200/90">
              "When a child speaks, we listen, we believe, we act."
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="spotlight-frame relative rounded-xl overflow-hidden border border-amber-500/25 shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500 max-w-xs mx-auto lg:max-w-none">
              <img
                src="/assets/3tier-network.webp"
                alt="P.A.I.N. 3-Tier Network — Predator Prevention Team, People Against Intentional Neglect, and Soldiers of P.A.I.N. ribbons"
                className="w-full h-auto object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 space-y-10">
        {INITIATIVES.map((it, i) => (
          <Reveal key={it.testId}>
            <article
              data-testid={it.testId}
              className={`card-pain p-6 sm:p-10 grid gap-8 lg:grid-cols-2 lg:items-center ${
                i % 2 === 1 ? "lg:[direction:rtl]" : ""
              }`}
            >
              <div className="lg:[direction:ltr]">
                {it.image && (
                  <div className="mb-6 rounded-xl bg-white/95 border border-amber-500/25 p-3 shadow-lg">
                    <img
                      src={it.image}
                      alt={`${it.name} logo`}
                      className="w-full h-44 object-contain"
                    />
                  </div>
                )}
                <span className="font-code text-xs uppercase tracking-[0.2em] text-amber-400/90">
                  {it.code}
                </span>
                <div className="mt-3 flex items-center gap-4">
                  <span className="flex items-center justify-center h-14 w-14 rounded-lg bg-[#1D4ED8]/20 border border-amber-500/25">
                    <it.icon className="h-7 w-7 text-amber-400" />
                  </span>
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-50">
                    {it.name}
                  </h2>
                </div>
                <div className="h-[2px] w-16 bg-gradient-to-r from-amber-400 via-amber-600 to-transparent my-4" />
                <p className="font-display text-lg italic text-amber-200/90">{it.tagline}</p>
                <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">{it.description}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to={`/initiatives/${
                      {
                        "initiative-ppt-module": "predator-prevention-team",
                        "initiative-soldiers-module": "soldiers-of-pain",
                        "initiative-crayons-module": "broken-crayons",
                        "initiative-born-module": "born-from-broken",
                      }[it.testId]
                    }`}
                    data-testid={`${it.testId}-explore-link`}
                    className="btn-gold-outline inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-amber-300"
                  >
                    Explore The Branch
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  {it.appUrl && (
                    <a
                      href={it.appUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="broken-crayons-app-link"
                      className="btn-royal inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white"
                    >
                      Open the Broken Crayons App
                    </a>
                  )}
                </div>
              </div>
              <div className="lg:[direction:ltr] rounded-lg border border-white/10 bg-[#0A0D14]/60 p-6">
                <Bullets items={it.bullets} />
              </div>
            </article>
          </Reveal>
        ))}
      </section>
    </div>
  );
}
